import { useEffect, useState } from 'react';
import { healthCheck } from '../services/api';

const CHECK_INTERVAL_MS = 5000;
const CHECK_TIMEOUT_MS = 3000;

/** Small header badge reflecting whether the backend is currently reachable - polls the
 * unauthenticated GET /health endpoint (no org/network context needed, unlike almost every
 * other API call in this app) on a short interval, independent of whatever page is active. */
export default function ConnectionStatusBadge() {
  const [connected, setConnected] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), CHECK_TIMEOUT_MS);
      try {
        await healthCheck(controller.signal);
        if (!cancelled) setConnected(true);
      } catch {
        if (!cancelled) setConnected(false);
      } finally {
        clearTimeout(timeoutId);
      }
    };

    check();
    const interval = setInterval(check, CHECK_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const label = connected === null ? 'Checking…' : connected ? 'Connected' : 'Disconnected';
  const color =
    connected === null
      ? 'bg-gray-500/20 text-gray-400 border-gray-500'
      : connected
      ? 'bg-green-500/20 text-green-400 border-green-500'
      : 'bg-red-500/20 text-red-400 border-red-500';

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${color}`}>
      <span className={`h-2 w-2 rounded-full ${connected === null ? 'bg-gray-400' : connected ? 'bg-green-400' : 'bg-red-400'}`} />
      {label}
    </span>
  );
}
